import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { localConfig, WS_URL } from '../config';

import * as fallbackConfig from '../config';
import { useDebugger } from './DebuggerContext';
import { Point } from '../system/location';

// Define interfaces for tenant configuration
export interface TenantConfig {
  tenant: string;
  config: Record<string, any>;
}

interface TenantContextType {
  tenant: string;
  isLoading: boolean;
  isTenantFoundInPath: boolean;
  hasProvidedTenantName: boolean;
  error: Error | null;
  tenantConfig: Record<string, any> | null;
  getServiceUrl: (serviceType?: string) => string;
  getConfigValue: <T>(key: string, defaultValue?: T) => T;
  getJsonConfigValue: <T>(key: string, defaultValue?: T) => T;
  getLocationConfigValue: (
    key: string,
    defaultValue?: Point,
  ) => Point | undefined;
  getTemplatedConfigValue: (
    key: string,
    defaultValue?: string,
  ) => string | undefined;
  initializeTenant: () => Promise<void>;
  isAddEditHost: () => boolean;
  isCotalitySystemTarget: () => boolean;
  supportsClientResoWebApi: () => boolean;
  supportsPromoteAuth: () => boolean;
  api: string; // Pre-computed API URL
  wsApi: string; // Pre-computed Websocket API URL
}

export const TenantContext = createContext<TenantContextType | undefined>(
  undefined,
);

/**
 * Extracts tenant name from URL (either from path or hostname)
 * Examples:
 * - https://addedit.mynextgensuite.com/mlsnow/ => 'mlsnow' (from path)
 * - https://mlsnow-laelocal.dev.brightmls.com/ => 'mlsnow' (from subdomain)
 * - https://mlsnow-lae.dev.brightmls.com/ => 'mlsnow' (from subdomain)
 */
const extractTenantFromUrl = (isAddEditHost: boolean): string | null => {
  const { pathname, hostname } = window.location;

  // Only extract from URL for addedit subdomain
  if (isAddEditHost) {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      return pathSegments[0];
    }
  }

  // Case 2: Extract from hostname patterns like "mlsnow-laelocal" or "mlsnow-lae"
  const subdomainPattern = /^([^-]+)-(laelocal|lae)\./;
  const subdomainMatch = hostname.match(subdomainPattern);

  if (subdomainMatch && subdomainMatch[1]) {
    return subdomainMatch[1];
  }

  return null;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { hostname } = window.location;

  const [tenant, setTenant] = useState<string>('bright'); // Default to "bright"
  const [tenantConfig, setTenantConfig] = useState<Record<string, any> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isTenantFoundInPath, setIsTenantFoundInPath] =
    useState<boolean>(false);
  const [hasProvidedTenantName, setHasProvidedTenantName] =
    useState<boolean>(false);
  const [needsAuthRedirect, setNeedsAuthRedirect] = useState<boolean>(false);

  const debug = useDebugger();

  const isAddEditHost = (): boolean =>
    hostname.startsWith('addedit.') || hostname.startsWith('addeditlocal.');

  /**
   * Determines tenant based on URL or defaults to "bright"
   */
  const determineTenant = useCallback((): string | undefined => {
    // First check if tenant is in URL (path or hostname)
    const urlTenant = extractTenantFromUrl(isAddEditHost());
    if (urlTenant) {
      debug.log('TenantContext', `Tenant found in URL: ${urlTenant}`);
      debug.groupEnd();
      return urlTenant;
    }
  }, [debug]);

  /**
   * Checks if a tenant name has been provided in the path (for addedit hosts)
   */
  const checkHasProvidedTenantName = useCallback((): boolean => {
    if (!isAddEditHost()) {
      // For non-addedit hosts, we don't need a tenant in the path
      return true;
    }

    const { pathname } = window.location;
    const pathSegments = pathname.split('/').filter(Boolean);

    // For addedit hosts, check if there's at least one path segment
    return pathSegments.length > 0;
  }, []);

  /**
   * Loads tenant-specific configuration from API
   */
  const loadTenantConfig = useCallback(
    async (tenantName: string): Promise<void> => {
      try {
        debug.log('TenantContext', `Loading config for tenant: ${tenantName}`);

        // Use temporary API URL for first request
        const defaultApiUrl = getServiceUrl('laeservices');

        const response = await fetch(
          `${defaultApiUrl}/get-tenant-config?tenant=${tenantName}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to load tenant config for ${tenantName}`);
        }

        const config: TenantConfig = await response.json();
        setTenantConfig(config.config);

        debug.log('TenantContext', 'Tenant config loaded successfully');
      } catch (err) {
        debug.log(
          'TenantContext',
          `Error loading tenant config: ${err}`,
          'error',
        );
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to load tenant config'),
        );
      }
    },
    [debug],
  );

  /**
   * Dynamically generates service URLs based on current domain and tenant
   * @param serviceType Optional subdomain prefix (defaults to 'laeservices')
   * @returns Fully qualified service URL
   */
  const getServiceUrl = useCallback(
    (serviceType: string = 'laeservices'): string => {
      // Extract base domain (e.g., brightmls.com)
      const domainParts = hostname.split('.');
      const subdomain = domainParts.shift(); // Remove and capture subdomain
      const baseDomain = domainParts.join('.');

      // Handle local development (map to dev services)
      if (hostname.includes('local.')) {
        if (localConfig.API_URL) {
          // TODO: easier config for local using .env files (coming in a future ticket)
          return localConfig.API_URL;
        }
      }

      // Check if current host has -legacy suffix
      const hasLegacySuffix = subdomain?.endsWith('-legacy');

      // Check if current host has -legacy suffix
      const hasSecondEnvSuffix = subdomain?.endsWith('2');

      // Add -legacy suffix to service type if current host has it
      const finalServiceType = hasLegacySuffix
        ? `${serviceType}-legacy`
        : hasSecondEnvSuffix
          ? `${serviceType}2`
          : serviceType;

      // Build URL with environment if needed
      return `https://${finalServiceType}.${baseDomain}`;
    },
    [determineTenant],
  );

  /**
   * Handles the case where addedit host has no tenant in path
   * Checks if user is authenticated and redirects to their tenant if so
   */
  const handleMissingTenantForAddEdit = useCallback(async (): Promise<
    string | null
  > => {
    try {
      // Try to get the session to see if user is authenticated
      const defaultApiUrl = getServiceUrl('laeservices');
      const response = await fetch(`${defaultApiUrl}/me`, {
        credentials: 'include',
      });

      if (response.ok) {
        const sessionData = await response.json();
        if (sessionData?.user?.tenant) {
          // User is authenticated, redirect to their tenant
          debug.log(
            'TenantContext',
            `Redirecting authenticated user to tenant: ${sessionData.user.tenant}`,
          );
          setNeedsAuthRedirect(true);
          // Small delay to prevent 404 flash
          setTimeout(() => {
            window.location.href = `/${sessionData.user.tenant}`;
          }, 100);
          return sessionData.user.tenant;
        }
      }
    } catch (err) {
      debug.log(
        'TenantContext',
        'Failed to check authentication for tenant redirect',
        'error',
      );
    }

    // User is not authenticated or no tenant found
    return null;
  }, [debug, getServiceUrl]);

  /**
   * Initializes tenant - called once at startup
   */
  const initializeTenant = useCallback(async (): Promise<void> => {
    if (isInitialized) return;

    try {
      setIsLoading(true);

      const hasProvidedName = checkHasProvidedTenantName();
      setHasProvidedTenantName(hasProvidedName);

      // Special handling for addedit host
      if (isAddEditHost()) {
        // Check if this is the auth-success route (special case)
        const { pathname } = window.location;
        const pathSegments = pathname.split('/').filter(Boolean);
        const firstSegment = pathSegments[0];

        // If the first segment is 'auth-success', try to get tenant from session
        if (firstSegment === 'auth-success') {
          debug.log(
            'TenantContext',
            'Auth-success route detected, checking session for tenant',
          );

          try {
            const defaultApiUrl = getServiceUrl('laeservices');
            const response = await fetch(`${defaultApiUrl}/me`, {
              credentials: 'include',
            });

            if (response.ok) {
              const sessionData = await response.json();
              if (sessionData?.user?.tenant) {
                debug.log(
                  'TenantContext',
                  `Using tenant from session: ${sessionData.user.tenant}`,
                );
                setTenant(sessionData.user.tenant);
                setIsTenantFoundInPath(true);
                await loadTenantConfig(sessionData.user.tenant);
                setIsInitialized(true);
                return;
              }
            }
          } catch (err) {
            debug.log(
              'TenantContext',
              'Failed to get tenant from session on auth-success',
              'warn',
            );
          }
        }

        // Handle normal case - no tenant in path
        if (!hasProvidedName) {
          debug.log('TenantContext', 'AddEdit host without tenant in path');

          // Try to get tenant from authenticated session
          const tenantFromSession = await handleMissingTenantForAddEdit();

          if (!tenantFromSession) {
            // No tenant available - AuthCoordinator will handle showing 404
            setIsTenantFoundInPath(false);
            setIsInitialized(true);
            return;
          }

          // If we get here, we're redirecting, so just wait
          return;
        }
      }

      const tenantName = determineTenant();
      const tenant = tenantName || 'bright'; // fallback to bright
      setIsTenantFoundInPath(typeof tenantName !== 'undefined');
      setTenant(tenant);

      // Only load config if we have a valid tenant
      await loadTenantConfig(tenant);
      setIsInitialized(true);
    } catch (err) {
      debug.log(
        'TenantContext',
        `Tenant initialization error: ${err}`,
        'error',
      );
      setError(
        err instanceof Error ? err : new Error('Failed to initialize tenant'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    determineTenant,
    loadTenantConfig,
    isInitialized,
    debug,
    checkHasProvidedTenantName,
    handleMissingTenantForAddEdit,
    getServiceUrl,
  ]);

  // Pre-compute the API URL
  const api = useMemo(() => getServiceUrl(), [getServiceUrl]);

  // Provide a helper for the application determine if local RESO web api calls are supported
  const supportsClientResoWebApi = (): boolean =>
    getConfigValue('SYSTEM_TYPE') === 'RESO';

  const supportsPromoteAuth = (): boolean =>
    getConfigValue<boolean>('ENABLE_PROMOTE', false) === true;

  const isCotalitySystemTarget = (): boolean =>
    getConfigValue<string>('MLS_TARGET', 'NONE')?.includes('COTALITY') ?? false;

  // Pre-compute the WebSocket API URL
  const wsApi = useMemo(() => {
    if (WS_URL) {
      return WS_URL;
    }

    const httpUrl = getServiceUrl();

    // Check if we're in local development
    if (httpUrl.includes('localhost') || httpUrl.includes('local.')) {
      // For local dev, serverless-offline uses port 3001 for WebSocket
      return httpUrl
        .replace(/^https:/, 'wss:')
        .replace(/^http:/, 'ws:')
        .replace(':3000', ':3001'); // Change port from 3000 to 3001
    }

    // For deployed environments, use the ws- subdomain pattern
    const wsUrl = httpUrl
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:')
      .replace(/^(wss?:\/\/)/, '$1ws-'); // Add ws- prefix after protocol

    return wsUrl;
  }, [getServiceUrl]);

  /**
   * Retrieves tenant-specific configuration values with fallback to default
   */
  const getConfigValue = useCallback(
    <T,>(key: string, defaultValue: T = (fallbackConfig as any)[key]): T => {
      if (!tenantConfig || !(key in tenantConfig)) {
        return defaultValue as T;
      }
      return tenantConfig[key] as T;
    },
    [tenantConfig],
  );

  /**
   * Retrieves a configuration value and parses it as a Point (x, y coordinates).
   *
   * Expects the configuration value to be a comma-separated string of two numbers
   * that will be parsed into x and y coordinates.
   *
   * @param key - The configuration key to retrieve
   * @param defaultValue - Optional default Point to return if the key doesn't exist or parsing fails
   * @returns A Point object with x and y coordinates, or the default value if parsing fails
   *
   * @example
   * // Config value: "45.5,-73.6"
   * getLocationConfigValue('defaultLocation')
   * // Returns: { x: 45.5, y: -73.6 }
   *
   * @example
   * // With default fallback
   * getLocationConfigValue('centerPoint', { x: 0, y: 0 })
   * // Returns: { x: 0, y: 0 } if 'centerPoint' is not in config
   *
   * @example
   * // Parsing various formats
   * getLocationConfigValue('mapCenter') // "40.7128, -74.0060" -> { x: 40.7128, y: -74.0060 }
   * getLocationConfigValue('origin')    // "0,0" -> { x: 0, y: 0 }
   * getLocationConfigValue('boundary')  // "-90.0, 180.0" -> { x: -90.0, y: 180.0 }
   *
   * @example
   * // Handles whitespace
   * getLocationConfigValue('userLocation') // " 51.5074 , -0.1278 " -> { x: 51.5074, y: -0.1278 }
   */
  const getLocationConfigValue = useCallback(
    (key: string, defaultValue?: Point): Point | undefined => {
      const value = getConfigValue(key);
      if (typeof value !== undefined) {
        const [x, y] = `${value}`.split(',').map((v) => parseFloat(v.trim()));

        return { x, y };
      }

      return defaultValue;
    },
    [getConfigValue],
  );

  /**
   * Retrieves a configuration value and parses it as JSON.
   */
  const getJsonConfigValue = useCallback(
    <T,>(key: string, defaultValue?: T): T => {
      const raw = getConfigValue<string>(key);
      if (!raw || typeof raw !== 'string') {
        return (defaultValue ?? raw) as T;
      }
      try {
        return JSON.parse(raw) as T;
      } catch {
        return defaultValue as T;
      }
    },
    [getConfigValue],
  );

  /**
   * Processes a template string by replacing placeholders with actual values.
   *
   * Supports multiple placeholder formats:
   * - `{tenant}` - Replaces with the current tenant name
   * - `{CONFIG_KEY}` - Replaces with the value from tenant config (or empty string if not found)
   * - `{CONFIG_KEY:fallback}` - Replaces with config value or the provided fallback
   * - `{CONFIG_KEY:nested.path}` - Supports nested config values using dot notation
   * - `{CONFIG_KEY:fallback with spaces}` - Fallback values can contain spaces
   * - `{{literal}}` - Escaped brackets (renders as `{literal}`)
   *
   * @param key - The configuration key to retrieve
   * @param defaultValue - Optional default value if the key doesn't exist
   * @returns The processed template string with all placeholders replaced, or undefined if key not found
   *
   * @example
   * // Basic tenant replacement
   * getTemplatedConfigValue('apiPath') // "/{tenant}/api" -> "/acme/api"
   *
   * @example
   * // Config value replacement
   * getTemplatedConfigValue('url') // "https://{DOMAIN}/path" -> "https://example.com/path"
   *
   * @example
   * // With fallback values
   * getTemplatedConfigValue('path') // "/api/{VERSION:v1}/users" -> "/api/v1/users" (if VERSION not set)
   *
   * @example
   * // Nested config values
   * getTemplatedConfigValue('msg') // "Welcome to {company.name:Our Company}" -> "Welcome to Acme Corp"
   *
   * @example
   * // Multiple replacements
   * getTemplatedConfigValue('full') // "/{tenant}/{ENV:prod}/{SERVICE:api}" -> "/acme/prod/api"
   *
   * @example
   * // Escaped brackets
   * getTemplatedConfigValue('literal') // "Use {{tenant}} for tenant name" -> "Use {tenant} for tenant name"
   */
  const getTemplatedConfigValue = useCallback(
    (key: string, defaultValue?: string): string | undefined => {
      // Get the raw value from config
      const rawValue = getConfigValue(key, defaultValue);

      // If no value found, return undefined
      if (rawValue === undefined || rawValue === null) {
        return undefined;
      }

      // Convert to string for processing
      let template = String(rawValue);

      // Helper function to get nested config values
      const getNestedConfigValue = (path: string): any => {
        const keys = path.split('.');
        let value: any = tenantConfig;

        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return undefined;
          }
        }

        return value;
      };

      // Replace escaped brackets first ({{...}} -> {TEMP_ESCAPED_...})
      const escapedPlaceholders: Map<string, string> = new Map();
      let escapeIndex = 0;
      template = template.replace(/\{\{([^}]+)\}\}/g, (match, content) => {
        const placeholder = `{TEMP_ESCAPED_${escapeIndex++}}`;
        escapedPlaceholders.set(placeholder, `{${content}}`);
        return placeholder;
      });

      // Process template placeholders
      template = template.replace(/\{([^}]+)\}/g, (match, placeholder) => {
        // Check if this is a temporary escaped placeholder
        if (placeholder.startsWith('TEMP_ESCAPED_')) {
          return match; // Will be replaced back later
        }

        // Handle special case: {tenant}
        if (placeholder.toLowerCase() === 'tenant') {
          return tenant;
        }

        // Parse placeholder for config key and optional fallback
        // Format: CONFIG_KEY or CONFIG_KEY:fallback
        const colonIndex = placeholder.indexOf(':');
        let configKey: string;
        let fallbackValue = '';

        if (colonIndex !== -1) {
          configKey = placeholder.substring(0, colonIndex).trim();
          fallbackValue = placeholder.substring(colonIndex + 1); // Don't trim to preserve intended spaces
        } else {
          configKey = placeholder.trim();
        }

        // Try to get the config value
        let configValue: any;

        // Check if it's a nested path (contains dots)
        if (configKey.includes('.')) {
          configValue = getNestedConfigValue(configKey);
        } else {
          // Direct config key lookup
          configValue = tenantConfig?.[configKey];
        }

        // Handle different types of config values
        if (configValue !== undefined && configValue !== null) {
          // If it's an object or array, JSON stringify it
          if (typeof configValue === 'object') {
            try {
              return JSON.stringify(configValue);
            } catch {
              return fallbackValue;
            }
          }
          // Convert to string
          return String(configValue);
        }

        // Return fallback value if config not found
        return fallbackValue;
      });

      // Restore escaped brackets
      escapedPlaceholders.forEach((original, placeholder) => {
        template = template.replace(placeholder, original);
      });

      return template;
    },
    [getConfigValue, tenant, tenantConfig],
  );

  // Initialize on component mount
  useEffect(() => {
    if (!isInitialized && !needsAuthRedirect) {
      initializeTenant();
    }
  }, [isInitialized, initializeTenant, needsAuthRedirect]);

  // Create memoized context value
  const contextValue = useMemo<TenantContextType>(
    () => ({
      tenant,
      isTenantFoundInPath,
      hasProvidedTenantName,
      isLoading,
      error,
      tenantConfig,
      isAddEditHost,
      isCotalitySystemTarget,
      getServiceUrl,
      getConfigValue,
      getJsonConfigValue,
      getLocationConfigValue,
      initializeTenant,
      getTemplatedConfigValue,
      supportsClientResoWebApi,
      supportsPromoteAuth,
      api,
      wsApi,
    }),
    [
      tenant,
      isTenantFoundInPath,
      isCotalitySystemTarget,
      hasProvidedTenantName,
      isLoading,
      error,
      tenantConfig,
      getServiceUrl,
      getConfigValue,
      getJsonConfigValue,
      getLocationConfigValue,
      getTemplatedConfigValue,
      supportsClientResoWebApi,
      supportsPromoteAuth,
      initializeTenant,
      api,
      wsApi,
    ],
  );

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * Convenience hook to directly access the API URL
 */
export const useApiUrl = (): string => {
  const { api } = useTenant();
  return api;
};
